import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { CUSTOMER_DATA_TAB } from 'cpool-constants';
import { CUSTOMER_DATA_TABS } from 'cpool-constants';

import { createRequiredContext } from '~/utils/createRequiredContext.tsx';

type TabsSetter = {
    setTab: Dispatch<SetStateAction<CUSTOMER_DATA_TAB>>;
    setNotification: ({
        notification,
        prefix,
        tab,
    }: {
        tab: CUSTOMER_DATA_TAB;
        prefix?: string;
        notification: string;
    }) => void;
    setNotifications: ({
        notifications,
        prefix,
        tab,
    }: {
        tab: CUSTOMER_DATA_TAB;
        prefix?: string;
        notifications: Array<string>;
    }) => void;
    removeNotification: ({
        notification,
        prefix,
        tab,
    }: {
        tab: CUSTOMER_DATA_TAB;
        prefix?: string;
        notification: string;
    }) => void;
};

type TabsGetter = {
    tab: CUSTOMER_DATA_TAB;
    notifications: Record<CUSTOMER_DATA_TAB, Array<string>>;
    hasNotifications: boolean;
};

const [useCustomerDataTabsContext, CustomerDataTabsProvider] = createRequiredContext<TabsSetter & TabsGetter>(
    'CustomerDataTabsContext',
);

type Props = {
    children: React.ReactNode;
    onTabChange?: (tab: CUSTOMER_DATA_TAB) => void;
    selectedTab?: CUSTOMER_DATA_TAB;
};

const CustomerDataTabsContextProvider: React.FC<Props> = ({ children, onTabChange, selectedTab }) => {
    const [internalTab, setInternalTab] = useState<CUSTOMER_DATA_TAB>(CUSTOMER_DATA_TABS.PERSONAL_DATA);
    const tab = selectedTab ?? internalTab;
    const tabRef = useRef(tab);
    tabRef.current = tab;
    const [notifications, setNotifications] = useState<Record<CUSTOMER_DATA_TAB, Array<string>>>({
        [CUSTOMER_DATA_TABS.PERSONAL_DATA]: [],
        [CUSTOMER_DATA_TABS.INCOME_AND_EXPENSES]: [],
        [CUSTOMER_DATA_TABS.EMPLOYMENT]: [],
        [CUSTOMER_DATA_TABS.LIVING_SITUATION]: [],
        [CUSTOMER_DATA_TABS.REAL_ESTATE]: [],
    });

    const setTab: Dispatch<SetStateAction<CUSTOMER_DATA_TAB>> = useCallback(
        (nextTab) => {
            const resolvedTab = typeof nextTab === 'function' ? nextTab(tabRef.current) : nextTab;
            if (selectedTab === undefined) setInternalTab(resolvedTab);
            onTabChange?.(resolvedTab);
        },
        [onTabChange, selectedTab],
    );

    // NOTE: for better rendering performance and less issues with useEffects, we separate the set and get functions
    const set: TabsSetter = useMemo(
        () => ({
            removeNotification: ({ notification, prefix, tab: dataTab }) => {
                setNotifications((prev) => ({
                    ...prev,
                    [dataTab]: prev[dataTab].filter(
                        (item) => item !== (prefix ? `${prefix}-${notification}` : notification),
                    ),
                }));
            },
            setNotification: ({ notification, prefix, tab: dataTab }) => {
                setNotifications((prev) => ({
                    ...prev,
                    [dataTab]: prev[dataTab].includes(prefix ? `${prefix}-${notification}` : notification)
                        ? prev[dataTab]
                        : [...prev[dataTab], prefix ? `${prefix}-${notification}` : notification],
                }));
            },
            setNotifications: ({ notifications: newNotifications, prefix, tab: dataTab }) => {
                setNotifications((prev) => {
                    const preparedNotifications: Array<string> = prefix
                        ? prev[dataTab].filter((item) => !item.startsWith(prefix))
                        : [];
                    return {
                        ...prev,
                        [dataTab]: [
                            ...preparedNotifications,
                            ...newNotifications.map((notification) =>
                                prefix ? `${prefix}-${notification}` : notification,
                            ),
                        ],
                    };
                });
            },
            setTab,
        }),
        [setTab],
    );

    const get: TabsGetter = useMemo(
        () => ({
            hasNotifications:
                notifications[CUSTOMER_DATA_TABS.PERSONAL_DATA].length > 0 ||
                notifications[CUSTOMER_DATA_TABS.INCOME_AND_EXPENSES].length > 0 ||
                notifications[CUSTOMER_DATA_TABS.EMPLOYMENT].length > 0 ||
                notifications[CUSTOMER_DATA_TABS.LIVING_SITUATION].length > 0 ||
                notifications[CUSTOMER_DATA_TABS.REAL_ESTATE].length > 0,
            notifications,
            tab,
        }),
        [notifications, tab],
    );

    return <CustomerDataTabsProvider value={{ ...set, ...get }}>{children}</CustomerDataTabsProvider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export { useCustomerDataTabsContext };

export default CustomerDataTabsContextProvider;
